import * as React from 'react';

import { useBoolean, useLockFn, useRequest } from 'ahooks';
import {
  Badge,
  Button,
  Divider,
  Form,
  Modal,
  notification,
  Select,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import { CheckOutlinedIcon, DeleteOutlinedIcon } from 'components';
import { useProductValue } from 'context';
import { verifyTokenPermission } from 'lib';
import { deleteProduct } from 'services';
import type { ProductStatus } from 'types';
import { localizeProductStatus, PATHNAME } from 'utils';

const {
  LOCAL: {
    PRODUCTS: { BASE_URL: PRODUCTS_BASE_URL },
  },
} = PATHNAME;

const productStatuses = [
  'Submitted',
  'Draft',
  'Private',
] satisfies ProductStatus[];

type FieldType = Pick<ReturnType<typeof useProductValue>, 'status'>;

const ProductFormAction = () => {
  const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();
  const [deleteLoading, { toggle: toggleDeleteLoading }] = useBoolean();
  const { id, defaultStatus, mode } = useProductValue();
  const { data: hasPublishPermission, loading: isPublishPermissionLoading } =
    useRequest(async () => await verifyTokenPermission(['product:publish']));
  const navigate = useNavigate();

  const handleDeleteProduct = useLockFn(
    async ({
      id,
    }: Pick<Required<ReturnType<typeof useProductValue>>, 'id'>) => {
      toggleDeleteLoading();

      deleteProduct({ id })
        .then(() => {
          navigate(PRODUCTS_BASE_URL);
          notification.success({
            message: 'محصول با موفقیت حذف شد.',
          });
        })
        .catch(message => {
          notification.error({
            message,
          });
        })
        .finally(() => {
          toggleDeleteLoading();
        });
    },
  );

  const { label: defaultStatusLabel, color: defaultStatusColor } =
    localizeProductStatus(defaultStatus);

  return (
    <div>
      <div className='px-6 pt-4'>
        <Typography.Title level={3} className='mb-0 text-base font-medium'>
          انتشار محصول
        </Typography.Title>
      </div>

      <Divider className='mb-8 mt-4' />

      <div className='px-6'>
        <Typography.Paragraph>
          <Badge
            text={`وضعیت فعلی: ${defaultStatusLabel}`}
            color={defaultStatusColor}
          />
        </Typography.Paragraph>
        <Form.Item<FieldType>
          label='وضعیت جدید'
          help={
            !hasPublishPermission
              ? 'شما دسترسی لازم برای انتشار محصول را ندارید.'
              : null
          }
          name='status'
          className='mb-2'
        >
          <Select
            allowClear
            placeholder='وضعیت جدید محصول را انتخاب کنید'
            loading={isPublishPermissionLoading}
            options={productStatuses.map(status => {
              const { label } = localizeProductStatus(status);
              const isSubmitted = status === 'Submitted';
              const isDisabled = isSubmitted && !hasPublishPermission;

              return {
                label,
                value: status,
                disabled: isDisabled,
              };
            })}
          />
        </Form.Item>
      </div>

      <div className='flex flex-wrap-reverse items-center gap-2 p-6 *:m-0 *:shrink *:grow *:basis-40'>
        {mode === 'Update' ? (
          <Form.Item>
            <Button
              block
              danger
              className='flex items-center'
              loading={deleteLoading}
              icon={<DeleteOutlinedIcon />}
              onClick={async () => {
                const confirmed = await deleteModalInstance.confirm({
                  title: 'آیا از حذف این محصول اطمینان دارید؟',
                  width: 500,
                });
                if (id && confirmed) {
                  handleDeleteProduct({ id });
                }
              }}
            >
              <span>حذف محصول</span>
            </Button>
            {deleteModalContextHolder}
          </Form.Item>
        ) : null}

        <Form.Item>
          <Button
            block
            type='primary'
            htmlType='submit'
            icon={<CheckOutlinedIcon />}
          >
            {mode === 'Create' ? (
              <span>افزودن محصول جدید</span>
            ) : (
              <span>ذخیره تغییرات</span>
            )}
          </Button>
        </Form.Item>
      </div>
    </div>
  );
};

export { ProductFormAction };
